import React, { useEffect, useState } from 'react';
import { Sparkles, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MOODS = [
  { id: 'peaceful',    emoji: '😌', label: 'Peaceful' },
  { id: 'grateful',    emoji: '🙏', label: 'Grateful' },
  { id: 'hopeful',     emoji: '🌱', label: 'Hopeful' },
  { id: 'motivated',   emoji: '🔥', label: 'Motivated' },
  { id: 'tired',       emoji: '😴', label: 'Tired' },
  { id: 'anxious',     emoji: '😣', label: 'Anxious' },
  { id: 'overwhelmed', emoji: '🌊', label: 'Overwhelmed' },
  { id: 'sad',         emoji: '💧', label: 'Sad' },
  { id: 'frustrated',  emoji: '😤', label: 'Frustrated' },
  { id: 'hurting',     emoji: '💛', label: 'Hurting' },
  { id: 'lonely',      emoji: '🌙', label: 'Lonely' },
];

// Old IDs (from before this label refresh) — kept only so we can render a
// legacy user's already-logged check-in without a fallback blank. New
// check-ins always use the IDs above.
const LEGACY_MOODS = {
  calm:       { emoji: '😌', label: 'Peaceful' },
  good:       { emoji: '🙂', label: 'Good' },
  okay:       { emoji: '😐', label: 'Okay' },
  low:        { emoji: '😔', label: 'Low' },
  stressed:   { emoji: '😣', label: 'Anxious' },
  happy:      { emoji: '🙂', label: 'Good' },
  reflective: { emoji: '😐', label: 'Reflective' },
};

const MoodCheckInCard = ({ onCheckIn }) => {
  const { user } = useAuth();
  const [todays, setTodays] = useState(null); // { checkedIn, entry }
  const [submitting, setSubmitting] = useState(null);

  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem('rbr_token');
    fetch(`${API_URL}/api/mood/today`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setTodays(d))
      .catch(() => {});
  }, [user]);

  if (!user) return null;

  const check = async (moodId) => {
    setSubmitting(moodId);
    try {
      const token = localStorage.getItem('rbr_token');
      const res = await fetch(`${API_URL}/api/mood/check-in`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ mood: moodId }),
      });
      if (!res.ok) throw new Error('check-in failed');
      const data = await res.json();
      setTodays({ checkedIn: true, entry: data.entry });
      toast.success('Mood logged for today');
      // Notify parent (e.g. TodayPersonalized) so it can refresh recommendations.
      if (typeof onCheckIn === 'function') {
        try { onCheckIn(moodId); } catch { /* never let parent crash us */ }
      }
    } catch (e) {
      toast.error('Could not log mood');
    } finally {
      setSubmitting(null);
    }
  };

  if (todays?.checkedIn) {
    const moodId = todays.entry?.mood;
    const m = MOODS.find((x) => x.id === moodId) || LEGACY_MOODS[moodId] || null;
    return (
      <div
        className="flex items-center gap-3 bg-[#FFFCF3] border border-stone-200 rounded-2xl p-3.5 shadow-sm"
        data-testid="mood-checkin-done"
      >
        <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center">
          <Check className="w-4 h-4 text-emerald-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-stone-800 truncate">
            Mood logged today {m ? `· ${m.emoji} ${m.label}` : ''}
          </p>
          <p className="text-[11px] text-stone-500">Saved to your journal</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="bg-[#FFFCF3] border border-stone-200 rounded-2xl p-4 shadow-sm"
      data-testid="mood-checkin-card"
    >
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-4 h-4 text-[#D6AD60]" />
        <p className="text-xs font-semibold text-[#D6AD60] uppercase tracking-wider">
          How are you feeling?
        </p>
      </div>
      <div className="grid grid-cols-4 gap-2">
        {MOODS.map((m) => (
          <button
            key={m.id}
            onClick={() => check(m.id)}
            disabled={submitting !== null}
            className="flex flex-col items-center gap-1 px-1.5 py-2.5 rounded-xl bg-[#E8DDCB] hover:bg-[#DBC9A3] transition-all duration-200 active:scale-95 disabled:opacity-50"
            data-testid={`mood-checkin-${m.id}`}
            aria-label={`Log mood: ${m.label}`}
          >
            <span className="text-2xl leading-none">{m.emoji}</span>
            <span className="text-[10px] text-stone-600 font-medium leading-tight text-center">{m.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default MoodCheckInCard;
